// Content for the pages rendered by sections/InfoPage.tsx. Same rule as
// faqs.ts: only facts that are true of our real work. Prices come from
// data/prices.ts (published since 2026-10, Alan's decision 2026-09-28). No
// client results or numbers (Alan's decision 2026-09-19: client data stays
// off the site until each client agrees).

import { contractorFaqs, designFaqs, socialFaqs, webDesignFaqs, type FaqItem } from "./faqs";
import {
  ADDRESS_PRICES,
  CARE_PLANS,
  DESIGN_PRICES,
  FREE_MONTH,
  HOSTING_SPECS,
  HOURLY,
  OTHER_PRICES,
  SEO_PRICES,
  SOCIAL_TIERS,
  WEBSITE_PRICES,
  type PriceRow,
} from "./prices";

// Shown at the top of the Terms and Privacy pages.
const LEGAL_UPDATED = "November 4, 2026";

export type Block = {
  title: string; // empty: no heading
  body?: string[];
  highlights?: { label: string; price: string; desc: string }[];
  order?: boolean; // the Order Online line, for a block with no price list of its own
  plans?: { name: string; price: string; desc: string; features: string[] }[];
  steps?: { title: string; desc: string }[];
  callout?: { text: string; label: string; href: string };
  prices?: PriceRow[];
  cards?: { title: string; desc: string }[];
  checklist?: string[];
  sites?: { name: string; trade: string; url: string; image?: string; detail: string }[];
  links?: { label: string; href: string }[];
};

export type InfoPageData = {
  file: string;
  h1: string;
  lead: string;
  lead2?: string;
  jump?: boolean; // a row of links to each section, for long pages
  plain?: boolean; // legal text: numbered sections, no icons
  blocks: Block[];
  faqs: FaqItem[];
  ctaTitle: string;
  ctaText: string;
};

// Question pages: each answers one thing people ask before hiring us and links
// back to the service page it supports (Alan approved 2026-10-28, not a blog).
const QUESTION_LINKS = [
  { label: "Do I need a website if I have a Facebook page?", href: "/website-vs-facebook.html" },
  { label: "How do I get my business on Google Maps?", href: "/google-maps-listing.html" },
  { label: "Who owns my website and domain name?", href: "/who-owns-my-website.html" },
];

export const CLIENT_SITES: NonNullable<Block["sites"]> = [
  {
    name: "Stacy's Skillet",
    trade: "Southern & Creole restaurant, Columbia",
    url: "https://stacysskillet.com",
    image: "/work/stacys-skillet.webp",
    detail:
      "Full house menu and lunch specials on their own pages, hours that match Google, a map, a DoorDash order button, and a job application form.",
  },
  {
    name: "Sorrell's Services",
    trade: "Excavation & land clearing, Columbia",
    url: "https://sorrellsservices.com",
    image: "/work/sorrells-services.webp",
    detail:
      "A page for each service, from excavation and ponds to house pads, a page for each town they work in, and an equipment page with a mini excavator rental request form.",
  },
  {
    name: "3D Land Management",
    trade: "Forestry mulching & land clearing, Columbia",
    url: "https://3dmanagement.land",
    image: "/work/3d-land-management.webp",
    detail:
      "A dozen service pages, a page for every county and town they serve across South Mississippi, and an equipment page showing their machines.",
  },
  {
    name: "MRA Construction",
    trade: "Carpentry, repairs & outdoor builds, Sumrall",
    url: "https://mra.construction",
    image: "/work/mra-construction.webp",
    detail:
      "Separate pages for every trade and outdoor project, from decks and docks to drywall, a page for each town, their own project photos, and a careers page.",
  },
];

const PREVIEW_CALLOUT: Block = {
  title: "",
  callout: {
    text: "We build free website previews for local businesses before anyone asks. We may have already built yours.",
    label: "Search your business",
    href: "https://leads.rare-works.com/",
  },
};

const AREA_FAQ: FaqItem = {
  q: "Where are you located?",
  a: "Rare Works is based in Columbia, MS and works with small businesses across South Mississippi, including Hattiesburg, Purvis, Petal, and McComb.",
};

function townPage(
  town: string,
  file: string,
  intro: string,
  local: string[],
  extraFaq: FaqItem,
): InfoPageData {
  return {
    file,
    h1: `Web Design in ${town}, MS`,
    lead: `Websites, local SEO, social media, and design for small businesses in ${town}, from a studio in Columbia, MS. ${intro}`,
    blocks: [
      { title: `A local studio for ${town} businesses`, body: local },
      {
        title: `What we do for ${town} businesses`,
        cards: [
          { title: "Websites", desc: "Custom designed and hand-coded, with a page for every service you offer and every town you work in. We host it and keep it updated." },
          { title: "Local SEO & Google", desc: "Your Google Business Profile set up right, your pages submitted to Google, and the searches you show up for tracked every week." },
          { title: "Social media", desc: "Posts for Facebook and Instagram made from your real work, on a regular schedule." },
          { title: "Logos & design", desc: "Logos, ad cards, flyers, signs, and menus that match your website." },
        ],
      },
      { title: "See our work", sites: CLIENT_SITES },
      PREVIEW_CALLOUT,
    ],
    faqs: [
      {
        q: `Do you work with businesses in ${town}?`,
        a: `Yes. We're based in Columbia, MS and ${town} is part of our regular South Mississippi area. We can meet in person, and everything else works by phone, text, or email.`,
      },
      extraFaq,
      {
        q: "How much does a website cost?",
        a: "A one-page starter site is $400, or $75 a month managed with no build fee. A business site with up to 10 pages is $1,800, or $150 a month managed. Every monthly plan starts with 30 days free. Our prices page lists everything.",
      },
      {
        q: "Do you use WordPress or templates?",
        a: "No. Every site is custom designed and hand-coded, with no WordPress, Squarespace, or drag-and-drop builders.",
      },
    ],
    ctaTitle: `Have a business in ${town}?`,
    ctaText: "Tell us what you do and we'll tell you which plan fits.",
  };
}

export const infoPages = {
  "web-design": {
    file: "web-design.html",
    h1: "Web Design & Management in Columbia, MS",
    lead: "We build your website from scratch: custom, fast, and actually yours. Buy it, or have us run it for one monthly price. Either way, you never touch code.",
    lead2: "Rare Works is a small web design studio based in Columbia, MS, building websites for small businesses across South Mississippi.",
    blocks: [
      {
        title: "Prices",
        body: [FREE_MONTH],
        highlights: [
          { label: "Managed", price: "From $75/mo", desc: "No build fee. We build it, host it, and make your changes. Buy it from us any time." },
          { label: "Own it", price: "From $400", desc: "Pay once and the site is yours. Add hosting from $5 a month, or a care plan and we make the changes." },
        ],
      },
      { title: "", prices: WEBSITE_PRICES },
      {
        title: "What's included",
        checklist: [
          "Custom design, no templates, ever",
          "Mobile-responsive on all devices",
          "Your own domain name, at cost, and you own it",
          "Hosting, SSL, and security on every plan",
          "First 30 days of any monthly plan free",
          "Edits made for you on managed and care plans",
          "SEO basics configured",
          "Contact form that emails you",
        ],
      },
      {
        title: "How it works",
        steps: [
          { title: "You send us what you have", desc: "Photos, logos, text, or tell us to create it all from scratch. Either works." },
          { title: "We build it", desc: "Custom designed and hand-coded. No WordPress, no Squarespace, no drag-and-drop." },
          { title: "You're live", desc: "We launch it, point your domain, and handle everything from there." },
        ],
      },
      { title: "Sites we've built", sites: CLIENT_SITES },
    ],
    faqs: webDesignFaqs,
    ctaTitle: "Ready to get started?",
    ctaText: "Send us a message and we'll get started.",
  },
  design: {
    file: "design.html",
    h1: "Design Services in Columbia, MS",
    lead: "Logos, ads, social graphics, and menus: anything visual your business needs. One price per job, no subscription.",
    lead2: "Rare Works designs logos, ads, and print pieces for small businesses in Columbia, MS and across South Mississippi.",
    blocks: [
      {
        title: "Prices",
        body: [`Need something that isn't listed? One-off design work is ${HOURLY.price}.`],
        prices: [
          { name: "Logo Design", price: DESIGN_PRICES["Logo Design"], desc: "Original logos that actually represent your business. Vector files and 2 rounds of changes included." },
          { name: "Ad Cards & Flyers", price: DESIGN_PRICES["Ad Cards & Flyers"], desc: "Print-ready designs for promos, events, menus, and handouts." },
          { name: "Social Media Graphics", price: DESIGN_PRICES["Social Media Graphics"], desc: "Facebook covers, Instagram posts, story templates, profile branding." },
          { name: "Business Cards", price: DESIGN_PRICES["Business Cards"], desc: "Clean, professional cards that match your brand. Print-ready PDF." },
          { name: "Menus & Signage", price: DESIGN_PRICES["Menus & Signage"], desc: "Restaurant menus, yard signs, and banners, in any format you need." },
          { name: "Brand Packages", price: DESIGN_PRICES["Brand Packages"], desc: "Logo and versions, colors, fonts, a business card, and a social kit, so you look the same everywhere." },
        ],
      },
      {
        title: "How it works",
        steps: [
          { title: "Tell us what you need", desc: "A logo, a flyer, posts for the week, whatever." },
          { title: "Send what you have", desc: "Photos, text, or brand colors, or let us create from scratch." },
          { title: "We deliver finished files", desc: "Print-ready, social-ready, whatever format you need." },
        ],
      },
    ],
    faqs: designFaqs,
    ctaTitle: "Need something designed?",
    ctaText: "Tell us what you're looking for and we'll get started.",
  },
  "social-media": {
    file: "social-media.html",
    h1: "Social Media Management in Columbia, MS",
    lead: "We handle your social media so you can focus on running your business. Content creation, scheduling, and engagement, all done for you.",
    lead2: "Rare Works is based in Columbia, MS and manages social media for small businesses across South Mississippi.",
    blocks: [
      { title: "Monthly plans", body: [FREE_MONTH], plans: SOCIAL_TIERS },
      {
        title: "One-off services",
        body: ["Don't need a monthly plan? We do individual tasks too."],
        prices: [
          { name: "Social media graphic", price: DESIGN_PRICES["Social Media Graphics"], desc: "A single post or cover designed for you." },
          { name: "Ad card", price: "$35", desc: "A Facebook or Instagram ad designed and ready to run." },
          { name: "Everything else", price: HOURLY.price, desc: "Content calendars, profile setup and branding, and other one-off tasks. 1-hour minimum." },
        ],
      },
    ],
    faqs: socialFaqs,
    ctaTitle: "Want us to run your social media?",
    ctaText: "Tell us about your business and we'll recommend the right plan.",
  },
  contractors: {
    file: "contractor-websites.html",
    h1: "Websites for Contractors in Mississippi",
    lead: "Most contractors get their work by word of mouth, then lose the customers who search Google first. We build and manage websites for contractors and land service businesses that show what you do, where you work, and make it easy to call or ask for a quote.",
    blocks: [
      {
        title: "Prices",
        body: [FREE_MONTH],
        highlights: [
          { label: "The build", price: "From $1,800", desc: "A business site with up to 10 pages, or $150 a month managed with no build fee. Bigger sites, with a page for every service and town, are quoted on scope at about $150 a page." },
          { label: "Ongoing", price: "Care from $50/mo", desc: "Hosting, security, backups, and edits. Growth care adds your Google Business Profile, local SEO, and a monthly report." },
        ],
        order: true,
      },
      {
        title: "What a contractor website needs",
        checklist: [
          "A page for each service you offer, so a search for one service lands on a page about it",
          "A page for each town or county you work in",
          "Your real photos and equipment, not stock images",
          "A quote form that emails you, and your phone number one tap away",
          "Buttons that make it easy for happy customers to leave a Google review",
          "Its own title and description for every page in Google results",
        ],
      },
      { title: "Contractor sites we've built", sites: CLIENT_SITES.slice(1) },
      PREVIEW_CALLOUT,
    ],
    faqs: contractorFaqs,
    ctaTitle: "Want a site like these?",
    ctaText: "Tell us about your business and we'll get started.",
  },
  "local-seo": {
    file: "local-seo.html",
    h1: "Local SEO & Google Business Profile in Columbia, MS",
    lead: "Getting found on Google when someone nearby searches for what you do. We set up and look after the pieces that decide it: your Google Business Profile, a page for every service and town, and the tracking that shows what's working.",
    blocks: [
      {
        title: "What local SEO means for a small business",
        body: [
          "When someone searches \"land clearing near me\" or \"deck builder Hattiesburg,\" Google shows a map with a few businesses and a list of websites. Local SEO is the work that gets your business into both: a complete, accurate Google Business Profile, and a website with a clear page for each thing you do and each place you do it.",
          "Most small businesses have a profile with the wrong category, no services listed, and a website with one page that tries to cover everything. Fixing that is where we start.",
        ],
      },
      { title: "What it costs", body: [FREE_MONTH], prices: SEO_PRICES },
      {
        title: "What we do",
        cards: [
          { title: "Google Business Profile", desc: "The right category, your services and products with links to the matching pages on your site, photos of your real work, hours, and regular posts for the towns you serve." },
          { title: "A page for every service and town", desc: "Someone searching for one service in one town should land on a page about exactly that, not your homepage." },
          { title: "Getting pages into Google", desc: "Google Search Console, a sitemap, and asking Google to check each new page so it shows up sooner." },
          { title: "Tracking calls and forms", desc: "Google Analytics set up to count phone taps and form submissions, not just visits." },
          { title: "Weekly keyword tracking", desc: "Which searches you show up for, where you rank, and new searches worth building a page for." },
          { title: "Monthly reports", desc: "A plain-English report each month on how people found you and what we're doing next." },
        ],
      },
      {
        title: "Also part of it",
        checklist: [
          "Your business name, address, and phone the same everywhere: Google, Facebook, Yelp, BBB, and local directories",
          "Review buttons on your site that go straight to your Google review form",
          "Unique page titles and descriptions for every page in Google results",
          "Pages that load fast and read well on a phone",
          "Checking what nearby competitors cover that you don't",
        ],
      },
      { title: "Guides", links: QUESTION_LINKS },
    ],
    faqs: [
      { q: "Can you guarantee I'll rank first on Google?", a: "No, and be careful with anyone who does. Nobody controls Google's rankings. What we can do is fix everything that's in your control and track the results every week." },
      { q: "How long does local SEO take?", a: "New pages usually show up in Google within days to a few weeks. Moving up for competitive searches takes longer, often a few months, and depends on your reviews and your competitors." },
      { q: "Do I need a website, or is a Google Business Profile enough?", a: "The profile gets you on the map, but the website is where people decide to call. The two work together: each service on your profile links to its own page on your site." },
      { q: "Can you fix my Google Business Profile if I already have one?", a: "Yes. Most of the profiles we see have the wrong category, no services, or out-of-date hours. You stay the owner; we just need to be added as a manager." },
      AREA_FAQ,
    ],
    ctaTitle: "Want to get found on Google?",
    ctaText: "Tell us about your business and we'll look at where you stand today.",
  },
  "our-work": {
    file: "our-work.html",
    h1: "Our Work",
    lead: "Websites we've designed, built, and manage for small businesses in South Mississippi. Every one is live. Click through and look around.",
    blocks: [
      { title: "Client websites", sites: CLIENT_SITES },
      {
        title: "Apps we've built",
        sites: [
          {
            name: "Skeulur",
            trade: "Employee scheduling app for restaurants",
            url: "https://skeulur.rare-works.com",
            image: "/work/skeulur.webp",
            detail:
              "Work schedules, time-off requests, and shift swaps, built for the way restaurants run. It installs on a phone like an app, and Stacy's Skillet runs its staff on it.",
          },
        ],
      },
      {
        title: "What they have in common",
        checklist: [
          "Custom designed and hand-coded, no templates",
          "The business's own photos, not stock images",
          "A page for each service and each town or county they serve",
          "Phone numbers one tap away and forms that email the owner",
          "Set up in Google Search Console and tracked every week",
          "Hosted, updated, and managed by us",
        ],
      },
    ],
    faqs: [
      { q: "Can my site look like one of these?", a: "Your site gets its own design built around your business, but yes, it can have the same kind of pages and features as any of these." },
      { q: "Do you only build websites for contractors and restaurants?", a: "No. Those are the businesses we've worked with most, but we build for small businesses of all kinds." },
      AREA_FAQ,
    ],
    ctaTitle: "Want a site like these?",
    ctaText: "Tell us about your business and we'll get started.",
  },
  "website-cost": {
    file: "website-cost.html",
    jump: true,
    h1: "How Much Does a Small Business Website Cost?",
    lead: "Here are our actual prices. A one-page site starts at $400, or $75 a month if we manage it. Everything below is what we charge every customer, so you can see the number before you ever talk to us.",
    blocks: [
      { title: "Websites", body: [FREE_MONTH], prices: WEBSITE_PRICES },
      {
        title: "Monthly care plans",
        body: ["For a site you own. Managed sites already include their care."],
        prices: CARE_PLANS,
      },
      { title: "Web address", prices: ADDRESS_PRICES },
      { title: "Google Business Profile and local SEO", prices: SEO_PRICES },
      {
        title: "Social media",
        prices: SOCIAL_TIERS.map((t) => ({ name: t.name, price: t.price, desc: t.features.slice(0, 2).join(", ") })),
      },
      { title: "Design", prices: [{ name: "Logo Design", price: DESIGN_PRICES["Logo Design"] }, { name: "Brand Packages", price: DESIGN_PRICES["Brand Packages"] }, { name: "Business Cards", price: DESIGN_PRICES["Business Cards"] }, { name: "Ad Cards & Flyers", price: DESIGN_PRICES["Ad Cards & Flyers"] }, { name: "Menus & Signage", price: DESIGN_PRICES["Menus & Signage"] }, { name: "Social Media Graphics", price: DESIGN_PRICES["Social Media Graphics"] }] },
      { title: "Everything else", prices: OTHER_PRICES },
      {
        title: "What changes the price",
        cards: [
          { title: "How many pages", desc: "A page for each service and each town you serve is what gets you found, and it's also the biggest part of the work. Past 10 pages, we quote the site." },
          { title: "Buy it or have us manage it", desc: "Buying costs more up front and less each month. Managed has no build fee, and you can buy the site from us later." },
          { title: "How much help you want after launch", desc: "Hosting only if you'll make your own changes, or a care plan if you want us to make them." },
          { title: "Features", desc: "Quote forms and contact forms are part of a normal site. Booking, customer portals, and apps are custom builds, quoted by the job." },
        ],
      },
      {
        title: "Questions to ask any web designer",
        checklist: [
          "Is hosting included, and what does it cost after the first year?",
          "Who owns the domain name?",
          "Are edits after launch included, or billed by the hour?",
          "Is it a template or a custom design?",
          "Will each service and town I serve get its own page?",
          "Will it be set up in Google Search Console?",
        ],
      },
      { title: "Guides", links: QUESTION_LINKS },
    ],
    faqs: [
      { q: "Is there really no build fee on a managed site?", a: "Right. On a managed plan you pay the monthly price and we build, host, and update the site. We own the site while you're on the plan, and you can buy it from us any time at the listed price." },
      { q: "What does the first 30 days free cover?", a: "Every monthly plan: managed sites, care plans, hosting, Google profile management, and social media. One-time fees like a site build or a logo are paid up front." },
      { q: "Who owns my domain name?", a: "You do. We charge what the registrar charges, with no markup." },
      { q: "Do you give refunds?", a: "One-time orders can be cancelled for a full refund if we have not started work yet, except domain names and RareWorks addresses. Otherwise payments are final, which is why monthly plans start with 30 days free, so you can see the work before you pay for it." },
      { q: "What if I need more edits than my plan includes?", a: "Extra work is $75 an hour, billed in 15-minute steps after the first hour. Unused plan time doesn't roll over to the next month." },
      AREA_FAQ,
    ],
    ctaTitle: "Know what you want?",
    ctaText: "Tell us about your business and we'll get you started.",
  },
  "website-vs-facebook": {
    file: "website-vs-facebook.html",
    h1: "Do I Need a Website If I Have a Facebook Page?",
    lead: "Short answer: yes, if you want people who don't already follow you to find you. Facebook is good for keeping up with people who know you. A website is what shows up when a stranger searches Google for what you do.",
    blocks: [
      {
        title: "What a Facebook page does well",
        body: [
          "Facebook is where your regulars see your specials, new photos, and announcements. Posting there is worth doing.",
          "But your posts mostly reach people who already follow you, Facebook decides who sees each one, and the page lives on Facebook's terms. You can't give each service its own page, and you don't control how it shows up in Google.",
        ],
      },
      {
        title: "What a website does that Facebook can't",
        cards: [
          { title: "Shows up in Google searches", desc: "Someone searching \"stump removal Hattiesburg\" can land on a page about exactly that. A Facebook page can't have a page for each service and town." },
          { title: "Backs up your Google Business Profile", desc: "The profile puts you on the map. Each service on it can link to its own page on your site, where people decide to call." },
          { title: "Works without an account", desc: "Anyone can read it on any phone without logging in." },
          { title: "Answers questions before they call", desc: "Hours, the towns you cover, your services, and a quote form, all in one place." },
          { title: "Counts your calls", desc: "We track phone taps and form submissions, so you can see what the site brings in." },
          { title: "Belongs to you", desc: "Your domain name is always yours, and if you buy the site, the site is too." },
        ],
      },
      {
        title: "Our advice",
        body: [
          "Keep the Facebook page and keep posting. Add a website so new customers can find you on Google, and link the two to each other. If you want something small to start, a one-page site with your hours, phone, services, photos, and a map is enough to show up.",
        ],
      },
      { title: "Guides", links: QUESTION_LINKS.filter((l) => l.href !== "/website-vs-facebook.html") },
    ],
    faqs: [
      { q: "What's the cheapest way to get a website?", a: "A one-page starter site is $400 once plus a monthly plan, or $75 a month if we manage it with no build fee. Every monthly plan starts with 30 days free." },
      { q: "Can you run my Facebook page too?", a: "Yes. Social media plans start at $400 a month for regular posts on Facebook and Instagram." },
      { q: "Will a website get me more calls?", a: "It gives new customers a way to find you on Google and a reason to call. Nobody can promise a number, but we track every phone tap and form so you can see what it does." },
      AREA_FAQ,
    ],
    ctaTitle: "Ready for a website?",
    ctaText: "Tell us about your business and we'll tell you what you need, and what you don't.",
  },
  "google-maps-listing": {
    file: "google-maps-listing.html",
    h1: "How to Get Your Business on Google Maps",
    lead: "Your spot on Google Maps comes from a free Google Business Profile. Here's how to set one up, what matters most once it's live, and what we do for clients.",
    blocks: [
      {
        title: "Step by step",
        checklist: [
          "Search Google for your business name first. If a listing already shows up, claim it instead of making a new one, so you don't end up with two.",
          "Otherwise go to business.google.com and add your business with its real name, exactly as customers know it.",
          "Pick your primary category carefully. It's one of the biggest things Google uses to decide which searches you show up for.",
          "If customers come to you, add your address. If you go to them, set a service area and hide your address.",
          "Verify the business. Google will ask you to prove it's real, often with a short video of your shop, truck, or tools.",
          "Fill in hours, phone, website, services, and a description, then add photos of your real work.",
        ],
      },
      {
        title: "What moves you up once you're on the map",
        cards: [
          { title: "The right category", desc: "\"Excavating contractor\" and \"Landscaper\" show up for different searches. Pick the one that matches your main work, then add secondary ones." },
          { title: "Being close to the searcher", desc: "Google favors businesses near the person searching. You can't change that, which is why the other pieces matter." },
          { title: "Reviews", desc: "Real reviews from real customers, and replies to them. Ask every happy customer, and make it easy with a direct review link." },
          { title: "Accurate hours", desc: "Google is more likely to show a business that's open when someone searches." },
          { title: "A website that matches", desc: "Each service on your profile can link to its own page on your site, with the same name, address, and phone." },
          { title: "Staying active", desc: "New photos and regular posts show Google and customers that the business is running." },
        ],
      },
      {
        title: "Mistakes we see all the time",
        checklist: [
          "The wrong primary category, so you show up for the wrong searches or none",
          "No services listed",
          "Hours that don't match your website or Facebook page",
          "A phone number typed into a post, which Google can reject",
          "Two listings for the same business",
        ],
      },
      { title: "If you want us to do it", body: [FREE_MONTH], prices: SEO_PRICES },
      { title: "Guides", links: QUESTION_LINKS.filter((l) => l.href !== "/google-maps-listing.html") },
    ],
    faqs: [
      { q: "Is a Google Business Profile free?", a: "Yes. Google doesn't charge for the profile. You only pay if you hire someone like us to set it up or manage it." },
      { q: "Can I be on Google Maps without a storefront?", a: "Yes. Businesses that go to the customer set a service area and hide their address." },
      { q: "I already have a profile. Can you fix it?", a: "Yes. You stay the owner; we just need to be added as a manager. Most profiles we see need the category, services, or hours fixed." },
      { q: "How long until I show up?", a: "Once Google verifies the business, the profile can show up within days. Ranking higher for busy searches takes longer and depends on your reviews and your competitors." },
      AREA_FAQ,
    ],
    ctaTitle: "Want us to set it up?",
    ctaText: "Tell us about your business and we'll look at your profile, or help you start one.",
  },
  "who-owns-my-website": {
    file: "who-owns-my-website.html",
    h1: "Who Owns My Website and Domain Name?",
    lead: "Ask this before you hire anyone to build your site. Here's how ownership works with us, in plain terms, and what to check with any web designer.",
    blocks: [
      {
        title: "How it works with us",
        cards: [
          { title: "Your domain name is yours", desc: "Always, on every plan. We charge what the registrar charges, with no markup." },
          { title: "Buy the site and it's yours", desc: "A starter site is $400 and a business site up to 10 pages is $1,800. Once it's paid for, the site is yours." },
          { title: "Managed sites are ours until you buy them", desc: "A managed plan has no build fee, so we own the site while you're on the plan. You can buy it from us any time at the listed price." },
          { title: "Your Google profile stays yours", desc: "When we manage your Google Business Profile, you stay the owner and add us as a manager." },
        ],
      },
      {
        title: "Questions to ask any web designer",
        checklist: [
          "Whose name is the domain registered in?",
          "If I leave, do I get my site, and what does it cost?",
          "Who owns the logo, photos, and text you made for me?",
          "Who has the logins for my hosting and my Google Business Profile?",
          "What happens to my site if I stop paying the monthly fee?",
        ],
      },
      {
        title: "Why it matters",
        body: [
          "Whoever controls your domain name controls your web address and any email on it. Getting it back from someone else can take time and money. Ask before you sign, and get the answer in writing.",
        ],
      },
      { title: "Guides", links: QUESTION_LINKS.filter((l) => l.href !== "/who-owns-my-website.html") },
    ],
    faqs: [
      { q: "Do I own my website?", a: "If you buy it, yes. On a managed plan we own the site while you're on the plan, and you can buy it from us any time at the listed price. Your domain name is always yours." },
      { q: "Who owns my domain name?", a: "You do. We charge what the registrar charges, with no markup." },
      { q: "Do you give refunds?", a: "One-time orders can be cancelled for a full refund if we have not started work yet, except domain names and RareWorks addresses. Otherwise payments are final, which is why monthly plans start with 30 days free, so you can see the work before you pay for it." },
      AREA_FAQ,
    ],
    ctaTitle: "Questions about your site?",
    ctaText: "Tell us what you have now and we'll explain your options.",
  },
  "restaurant-websites": {
    file: "restaurant-websites.html",
    h1: "Restaurant Websites in Mississippi",
    lead: "Most people check a restaurant's menu and hours online before they go. We build restaurant websites that answer those questions fast, match what Google shows, and make it easy to order or call.",
    blocks: [
      {
        title: "What a restaurant website needs",
        checklist: [
          "The full menu on its own page, not a blurry photo of the paper one",
          "Specials and lunch menus that are easy to update",
          "Hours that match your Google listing, including split lunch and dinner hours",
          "A map and directions",
          "Your phone number one tap away, and your online ordering link front and center",
          "A job application form, so hiring doesn't run through Facebook messages",
          "Your own food and dining room photos",
        ],
      },
      { title: "What it costs", body: [FREE_MONTH], prices: WEBSITE_PRICES.slice(0, 4) },
      {
        title: "Also for restaurants",
        cards: [
          { title: "Google Business Profile", desc: "Menu link, hours, photos, and posts for your specials, kept in line with your website." },
          { title: "Social media", desc: "Posts for your specials and events on Facebook and Instagram, on a regular schedule." },
          { title: "Menus & print", desc: "Printed menus, table cards, and flyers that match your site." },
          { title: "Updates when things change", desc: "New prices, new specials, or holiday hours, updated for you on a managed or care plan." },
        ],
      },
      {
        title: "A restaurant site we built",
        sites: CLIENT_SITES.slice(0, 1),
      },
      PREVIEW_CALLOUT,
    ],
    faqs: [
      { q: "Can you update my menu when prices change?", a: "Yes. On a managed site or a care plan, send us the changes and we'll make them. A managed starter site includes up to 2 hours of changes a month." },
      { q: "Can customers order online from my site?", a: "We add a button to whatever ordering service you use, like DoorDash, so customers can order in one tap." },
      { q: "Do you design printed menus too?", a: "Yes. Menus, table cards, flyers, and signs are part of our design services." },
      AREA_FAQ,
    ],
    ctaTitle: "Run a restaurant?",
    ctaText: "Tell us about it and we'll tell you which plan fits.",
  },
  "care-plans": {
    file: "care-plans.html",
    h1: "Website Care Plans & Hosting",
    lead: "A website needs someone looking after it: hosting, security, backups, and changes when your hours or prices change. Pick how much of that you want us to do.",
    blocks: [
      { title: "The plans", body: [FREE_MONTH], prices: CARE_PLANS },
      {
        title: "What every plan includes",
        checklist: [
          "Fast hosting with an SSL certificate, so the padlock shows in the browser",
          "Security and uptime looked after for you",
          "A free web address, or your own domain name billed at cost with no markup, which you own",
          "Backups on every care plan",
          "No long-term contract. Cancel any time. A managed business site is the one exception, with a 12-month minimum.",
        ],
      },
      {
        title: "Your web address",
        body: ["Every site we host has a free address. A shorter one is optional."],
        prices: ADDRESS_PRICES,
      },
      {
        title: "Hosting details",
        body: ["Every plan, from Hosting only to Growth care, puts your site on the same hosting:"],
        checklist: HOSTING_SPECS,
      },
      {
        title: "How edits work",
        body: [
          "On a care plan, send us what you want changed by text or email and we make it: new hours, new prices, a new photo, a new service. Each plan includes a set amount of our time each month.",
          "If a month needs more than your plan includes, the extra is $75 an hour, billed in 15-minute steps after the first hour. Unused time doesn't roll over.",
          "On hosting only, the site is yours to change yourself, with your own FTP login. Hosting only needs your own domain name: bring one you already have, transfer it to us, or we register one for you at what it costs us.",
        ],
      },
      {
        title: "Rather not own the site?",
        body: [
          "A managed site has no build fee. A one-page site is $75 a month and includes up to 2 hours of changes. A business site with up to 10 pages is $150 a month with a 12-month minimum and includes Standard care. You can buy either one from us any time.",
        ],
      },
    ],
    faqs: [
      { q: "Do I need a care plan?", a: "Only if you want us to make changes for you. If you'd rather do it yourself, hosting only is $5 a month or $50 a year." },
      { q: "What counts as an edit?", a: "Changes to a site that already exists: text, prices, hours, photos, a new section. A brand new page is $150." },
      AREA_FAQ,
    ],
    ctaTitle: "Want someone looking after your site?",
    ctaText: "Tell us about your website and we'll tell you which plan fits.",
  },
  "custom-builds": {
    file: "custom-builds.html",
    h1: "Custom Web Apps, Booking & Portals",
    lead: "Sometimes a website needs to do more than show information: take bookings, give customers a login, or run part of your business. We build those from scratch, the same way we build our own software.",
    blocks: [
      {
        title: "What we build",
        cards: [
          { title: "Booking and scheduling", desc: "Customers pick a time, you get the appointment, and nobody plays phone tag." },
          { title: "Customer portals", desc: "A login where your customers see their own orders, requests, or account." },
          { title: "Business tools", desc: "Scheduling, order tracking, and other tools built around how your business actually runs." },
          { title: "Apps that work on a phone", desc: "Web apps that install on a phone's home screen, with no app store needed." },
        ],
      },
      {
        title: "How pricing works",
        body: [
          "Custom builds are quoted by the job, because no two are alike. We estimate at $85 an hour and give you a written quote before any work starts.",
          "Half is due up front as a deposit and half when it launches. After launch, hosting and care are on the same monthly plans as any other site.",
        ],
        prices: [WEBSITE_PRICES[WEBSITE_PRICES.length - 1]],
      },
      {
        title: "We build software of our own",
        body: [
          "Rare Works makes Skeulur, an employee scheduling app. It is live, and it is built with the same tools we'd use for yours.",
        ],
      },
    ],
    faqs: [
      { q: "How do I know what my project will cost?", a: "Tell us what you want it to do. We'll ask a few questions and send a written quote before any work starts." },
      { q: "Can you add booking to a site you already built for me?", a: "Yes. It's quoted as its own job and added to your existing site." },
      { q: "Do I need a custom build, or just a website?", a: "If you need to show what you do and get calls and form requests, a regular site covers it. If customers need to log in, book, or pay through it, that's a custom build." },
      AREA_FAQ,
    ],
    ctaTitle: "Have something in mind?",
    ctaText: "Tell us what you want it to do and we'll send a quote.",
  },
  // Legal pages. Source for the terms: alan-worksheets business/terms-draft.md,
  // brought in line with how billing works now (30 free days, then the same
  // day each month; late steps count from the missed bill). Not reviewed by a
  // lawyer. Change LEGAL_UPDATED whenever either page changes.
  terms: {
    file: "terms.html",
    plain: true,
    h1: "Terms and Conditions",
    lead: `Last updated ${LEGAL_UPDATED}. These terms are between you (the customer) and Alan Michael Morgan, doing business as RareWorks ("RareWorks", "we", "us"), based in Columbia, Mississippi. By buying anything from us, or checking the box at checkout, you agree to them.`,
    blocks: [
      {
        title: "What you are buying",
        body: ["Each product and plan includes exactly what its description on rare-works.com says at the time you buy it. Anything not listed is extra and billed at our hourly rate or by quote."],
      },
      {
        title: "Payments and billing",
        checklist: [
          "One-time fees (site builds, setup, design) are paid in full at checkout, except custom work (see section 6).",
          "Every monthly plan starts with 30 days free. Your first bill comes at the end of those 30 days, and the plan then bills on that same day each month. A yearly plan is paid up front for the year.",
          "Payments are taken by card through Stripe. We never see or store your full card number.",
          "Prices do not include any sales tax that applies.",
          "We may change prices for monthly plans with at least 60 days' notice by email.",
        ],
      },
      {
        title: "Refunds",
        body: ["All payments are final, with the one exception below. We do not give refunds for one-time fees, monthly or yearly plans, partial periods, or unused edit time. If you cancel, you keep the service until the end of the period you already paid for.", "Some things cost us money the moment you order them, so we want to be extra clear that they cannot be refunded: a domain name once it is registered or renewed, a RareWorks address once it is set up, a yearly plan once it starts, deposits on custom work, design work once we have started it, and any advertising money you pay to Facebook or Google.", "One exception: if you ordered a one-time service and we have not started work on it yet, you can cancel for a full refund. We usually start within minutes of getting an order, so call or email us right away. This does not apply to domain names or RareWorks addresses, which are set up as soon as you order."],
      },
      {
        title: "Cancelling a plan",
        checklist: [
          "You can cancel any time from your account or by emailing us. The plan ends at the end of the period you have paid for.",
          "Managed Business sites have a 12-month minimum. If you cancel before 12 months, the remaining months up to 12 are due, unless you buy out the site (section 5).",
        ],
      },
      {
        title: "Who owns what",
        checklist: [
          "Your domain name: you own it. If you choose one, it is registered for you and you can move it anywhere, any time. We will help you transfer it. Hosting only requires a domain name of your own.",
          "RareWorks addresses: every site we host has a free address at leads.rare-works.com. A shorter address on one of our domains (yourname.rare-works.com, .net or .xyz) is an optional add-on, paid by the year. Those addresses belong to RareWorks. One stops working if you do not renew it or your plan ends, and it may then be given to someone else.",
          "Your website on a managed plan: RareWorks owns the site we built (the design, code and layout) and licenses it to you while your plan is active. You can buy us out at any time for the current list price of that site (Starter $400, Business $1,800, custom sites at the price quoted). After a buyout you own the site files and can host them anywhere, or stay with us on Hosting only or a care plan.",
          "If you bought the site outright, you own it once it is paid for.",
          "Your content: your logo, photos, text and business information stay yours. You give us permission to use them to build and run your site and social media.",
          "We may show your site or work in our portfolio unless you ask us not to.",
        ],
      },
      {
        title: "Custom work",
        body: ["Custom builds are quoted in writing. 50% of the quote is due to start and is non-refundable. The other 50% is due at launch. Work outside the written quote is billed hourly."],
      },
      {
        title: "Late or failed payments",
        body: ["If a payment fails, we let you know and the card is tried again. If the bill is still unpaid 14 days after it was due, it is late. If it is still unpaid a month after it was due, we will contact you about it. If it is still unpaid 15 days after that, we may cancel your plan and take the site or service down. We restore it once the account is paid. A paid RareWorks address (like yourname.rare-works.com) may be given to someone else in the meantime."],
      },
      {
        title: "Your responsibilities",
        checklist: [
          "The information, photos and files you give us must be accurate and yours to use (or you have permission).",
          "You are responsible for what your business offers and says, including prices, hours and any legal claims.",
          "You will not ask us to publish anything illegal, misleading or that infringes someone else's rights.",
        ],
      },
      {
        title: "Design and changes",
        body: ["Design work includes the number of revision rounds listed on the product (for example, 2 rounds for a logo). More rounds are billed hourly. Monthly edit time does not roll over."],
      },
      {
        title: "Results",
        body: ["We work hard to help you show up on Google and social media, but no one controls Google, Facebook or other platforms. We do not guarantee rankings, traffic, followers, calls or sales."],
      },
      {
        title: "Social media and ads",
        body: ["Ad spend on Facebook, Google or other platforms is paid by you directly and is not part of our fee. You keep ownership of your social media accounts, and you must keep us as an admin while we manage them."],
      },
      {
        title: "Third-party services",
        body: ["Your site and services depend on other companies (hosting, payment processing, domain registrars, Google, Facebook). We are not responsible for their outages or policy changes, but we will help you work through them."],
      },
      {
        title: "Limit of liability",
        body: ["To the extent the law allows, our total responsibility for any claim is limited to the amount you paid us in the 3 months before the claim. We are not responsible for lost profits or indirect damages."],
      },
      {
        title: "Preview sites",
        body: ["Preview sites on leads.rare-works.com are built by us before the business asks, so the business can see what a site would look like. They are hidden from Google until a plan is bought. A business owner can claim a preview, or ask for it to be taken down, with proof they own the business."],
      },
      {
        title: "Changes to these terms",
        body: ["We may update these terms. The date at the top shows the latest version. For monthly plans, changes apply from your next billing period after we notify you."],
      },
      {
        title: "Mississippi law",
        body: ["These terms are governed by the laws of the State of Mississippi. Any dispute will be handled in the courts of Marion County, Mississippi."],
      },
      {
        title: "Contact",
        body: ["RareWorks, Columbia, MS. alan@rare-works.com. (601) 441-9656."],
        links: [{ label: "Privacy Policy", href: "/privacy.html" }, { label: "Prices", href: "/website-cost.html" }],
      },
    ],
    faqs: [],
    ctaTitle: "A question about these terms?",
    ctaText: "Ask before you buy. We would rather explain it than surprise you.",
  },
  privacy: {
    file: "privacy.html",
    plain: true,
    h1: "Privacy Policy",
    lead: `Last updated ${LEGAL_UPDATED}. This covers rare-works.com and leads.rare-works.com, run by Alan Michael Morgan, doing business as RareWorks, in Columbia, Mississippi. It says what we collect, why, and who else handles it. We do not sell your information.`,
    blocks: [
      {
        title: "What we collect",
        checklist: [
          "What you send us: your name, email, phone number and message when you use a contact form, ask for a site, claim a preview, or chat with us.",
          "Account details: if you are a customer, your business information, the changes you ask for, and any photos or files you upload.",
          "If you apply to be a sales rep: your name, cell, email, town, and anything you tell us about your experience and the businesses you know.",
          "Proof of ownership: a document you upload to claim a preview site or have one taken down, such as a business license. It is kept private and used only to confirm you run the business.",
          "Payment details: your card is entered on Stripe's own page. We receive your name, email, what you bought and whether it was paid. We never see or store your full card number.",
          "How the site is used: pages viewed, the kind of device, and the general area you are in, through Google Analytics.",
        ],
      },
      {
        title: "Information about businesses on preview sites",
        body: ["Preview sites on leads.rare-works.com are built from information a business has already made public, such as its Facebook page and Google listing: name, address, phone, hours, menu or services, and photos. Previews are hidden from Google until the owner buys a plan. If it is your business, you can claim the preview, ask us to change anything, or ask us to take it down."],
      },
      {
        title: "Why we use it",
        checklist: [
          "To answer you, quote work, and do the work you bought.",
          "To bill you and keep records of what was paid.",
          "To let you sign in to your account. We email a one-time link and code. There are no passwords.",
          "To review your application if you apply to be a sales rep.",
          "To see which pages are useful and fix the ones that are not.",
        ],
      },
      {
        title: "Who else handles it",
        body: ["We use other companies to run the business. Each one only gets what it needs for its job:"],
        checklist: [
          "Stripe: payments and billing.",
          "Supabase: the database behind customer accounts, requests and uploaded files.",
          "Formspree: delivers the contact form on rare-works.com to our email.",
          "Resend: sends sign-in emails.",
          "Google Analytics: visitor statistics on rare-works.com.",
          "Slack: our team is notified there when you send a request or buy something.",
          "Anthropic: when no one on our team is online, the chat on a preview site can be answered by an AI assistant, and the messages in that chat are processed by Anthropic to write the reply.",
          "Porkbun: website hosting and domain registration.",
        ],
      },
      {
        title: "Cookies and storage in your browser",
        body: ["Google Analytics sets cookies on rare-works.com to count visits. Your browser also keeps a small amount of data so you stay signed in to your account and so your cart is remembered. We do not use advertising cookies."],
      },
      {
        title: "How long we keep it",
        body: ["We keep customer and billing records for as long as you are a customer and for as long afterward as tax and business records require. Messages from people who never became customers are kept while they are useful and deleted on request."],
      },
      {
        title: "Your choices",
        checklist: [
          "Ask to see, correct or delete what we hold about you by emailing alan@rare-works.com.",
          "Change your business information yourself in your account.",
          "Ask for a preview site of your business to be taken down, from the link at the top of the preview.",
          "Block analytics cookies in your browser. The site still works.",
        ],
      },
      {
        title: "Children",
        body: ["Our services are for businesses. We do not knowingly collect information from children under 13."],
      },
      {
        title: "Our apps",
        body: ["Skeulur is a separate product and is not covered by this page. Where it collects information, its own policy applies."],
      },
      {
        title: "Changes and contact",
        body: ["If this policy changes, the date at the top changes with it. Questions: RareWorks, Columbia, MS. alan@rare-works.com. (601) 441-9656."],
        links: [{ label: "Terms and Conditions", href: "/terms.html" }],
      },
    ],
    faqs: [],
    ctaTitle: "A question about your information?",
    ctaText: "Email or call and we will tell you exactly what we have.",
  },
  "town-hattiesburg": townPage(
    "Hattiesburg",
    "web-design-hattiesburg-ms.html",
    "Just down Highway 98, close enough to meet in person.",
    [
      "Search for a Hattiesburg web designer and most of what comes up are agencies based in Jackson, Tupelo, or out of state, with a page that swaps in the town's name. Rare Works is a small studio in Columbia, MS, just down Highway 98.",
      "Several of the businesses we build for serve Hattiesburg: Sorrell's Services, 3D Land Management, and MRA Construction each have their own Hattiesburg page that we built. We know what people there search for because we track it for them.",
    ],
    { q: "Are you a Hattiesburg company?", a: "We're based in Columbia, MS, which is a short drive down Highway 98. Several of our clients serve Hattiesburg, and we're glad to meet you there." },
  ),
  "town-purvis": townPage(
    "Purvis",
    "web-design-purvis-ms.html",
    "We built and manage the website for a contractor that serves Purvis.",
    [
      "Rare Works is a small studio in Columbia, MS, and we already work in the Purvis area: we built and manage the website for MRA Construction, a Sumrall carpentry and home repair contractor that serves Purvis.",
      "That site has a page for each trade and outdoor project, a page for each town they serve, and their own project photos. We can build the same kind of site for your Purvis business.",
    ],
    { q: "Have you worked with businesses around Purvis?", a: "Yes. We built and manage the website for MRA Construction, a Sumrall contractor that serves Purvis, and track how people find them on Google." },
  ),
  "town-petal": townPage(
    "Petal",
    "web-design-petal-ms.html",
    "Our clients already serve Petal, and we build their Petal pages.",
    [
      "Rare Works is a small studio in Columbia, MS. Petal sits right next to Hattiesburg, and it's easy to get lost behind the bigger town in Google results. That's why our clients who work there, Sorrell's Services and MRA Construction, each have their own Petal page that we built.",
      "If your business is in Petal, your website should say so clearly, with pages built around the people searching there.",
    ],
    { q: "Why would a Petal business need its own town pages?", a: "People often search with the town name. A page about your service in Petal gives Google a clear answer, instead of hoping it connects Petal to a Hattiesburg page." },
  ),
  "town-mccomb": townPage(
    "McComb",
    "web-design-mccomb-ms.html",
    "Straight down Highway 98 from Columbia.",
    [
      "Rare Works is a small studio in Columbia, MS, straight down Highway 98 from McComb. One of our clients, 3D Land Management, serves Pike County and has its own McComb page that we built.",
      "We build the same kind of site for McComb businesses: a page for each thing you do, a page for each town you serve, and a Google Business Profile that matches.",
    ],
    { q: "Do you meet with McComb businesses in person?", a: "Yes, McComb is an easy drive from Columbia. Most of the work happens by phone, text, and email, but we're happy to meet." },
  ),
} satisfies Record<string, InfoPageData>;

export type InfoPageKey = keyof typeof infoPages;
